import { faCircleDot, faCopy, faEllipsisVertical, faGrip, faPen, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonNew as Button } from "components/buttonNew";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import {
  WEBSITE_BUILDER_TABSET_MAX_TABS,
  IWebsiteBuilderTabset,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { websiteBuilderGenerateDefaultTab } from "pages/secure/facility/websiteBuilder/editor/nodes/tab/websiteBuilderTab";
import { websiteBuilderSanitize } from "pages/secure/facility/websiteBuilder/editor/sanitization/websiteBuilderSanitize";
import React, { useCallback, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";
import {
  websiteBuilderAddTab,
  websiteBuilderDeleteTab,
  websiteBuilderSwapTab,
  websiteBuilderUpdateContainerSettings,
  websiteBuilderUpdateTabSettings,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import { useSectionEntityPreviewContext } from "pages/secure/facility/websiteBuilder/editor/designSpace/views/components/sectionEntity/SectionEntityPreviewContext";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";

interface ITabsetSettingsProps {
  node: IWebsiteBuilderTabset;
  closeSettings: () => void;
}

interface ITabsetSettingsRenameTabTitle {
  value: string;
  dirty: boolean;
  valid: boolean;
}

interface ITabsetSettingsRenameTabState {
  id: string;
  title: ITabsetSettingsRenameTabTitle;
}

export function TabsetSettings(props: ITabsetSettingsProps) {
  const { node, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { setPreview } = useSectionEntityPreviewContext();

  const { tabsetActiveTabMap } = useWebsiteBuilderEditorContext();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [renameTabState, setRenameTabState] = useState<ITabsetSettingsRenameTabState>(null);

  const renameTabRef = useOnclickOutside(() => {
    setRenameTabState(null);
  }, { ignoreClass: "tabset-settings-list-item-rename"});

  const settings = node.container.settings;

  const maxTabsReached = node.children.length >= WEBSITE_BUILDER_TABSET_MAX_TABS;

  const activeTabId = tabsetActiveTabMap.get(node.id);

  function setSelectedTab(tabId: string) {
    if (tabId == null) {
      setPreview(null);
    } else {
      setPreview({
        type: "container",
        container: {
          type: "tabset",
          selectedTabId: tabId,
        },
      });
    }
  }

  function addTab() {
    if (maxTabsReached) {
      return;
    }

    const tab = websiteBuilderGenerateDefaultTab(t("Tab {{tabNumber}}", {
      tabNumber: node.children.length + 1,
    }));

    const prevDefaultTabId = settings.defaultTabId;
    const nextDefaultTabId = prevDefaultTabId ?? tab.id;

    dispatch(
      websiteBuilderAddTab(activePageId, deviceView, node.id, {
        tabsetId: node.id,
        tab,
        index: node.children.length,
        prevDefaultTabId,
        nextDefaultTabId,
      }),
    );

    setSelectedTab(tab.id);
  }

  function duplicateTab(tabId: string) {
    if (maxTabsReached) {
      return;
    }

    const index = node.children.findIndex(tab => tab.id === tabId);

    if (index === -1) {
      return;
    }

    const duplicatedTab = websiteBuilderSanitize(node.children[index]);

    duplicatedTab.settings.title = t("Copy of {{title}}", { title: duplicatedTab.settings.title });

    const prevDefaultTabId = settings.defaultTabId;
    const nextDefaultTabId = prevDefaultTabId;

    dispatch(
      websiteBuilderAddTab(activePageId, deviceView, node.id, {
        tabsetId: node.id,
        tab: duplicatedTab,
        index: index + 1,
        prevDefaultTabId,
        nextDefaultTabId,
      }),
    );

    setSelectedTab(duplicatedTab.id);
  }

  function deleteTab(tabId: string) {
    const index = node.children.findIndex(tab => tab.id === tabId);

    if (index === -1) {
      return;
    }

    const adjacentTabId = node.children[index + 1]?.id ?? node.children[index - 1]?.id ?? null;

    const prevDefaultTabId = settings.defaultTabId;
    const nextDefaultTabId = tabId === prevDefaultTabId
      ? adjacentTabId
      : prevDefaultTabId;

    const nextSelectedTabId = tabId === activeTabId
      ? adjacentTabId
      : activeTabId;

    dispatch(
      websiteBuilderDeleteTab(activePageId, deviceView, node.id, {
        tabsetId: node.id,
        tab: cloneDeep(node.children[index]),
        index,
        prevDefaultTabId,
        nextDefaultTabId,
      }),
    );

    setSelectedTab(nextSelectedTabId);
  }

  const swapTab = useCallback((prevIndex: number, nextIndex: number) => {
    if (prevIndex === nextIndex) {
      return;
    }

    dispatch(
      websiteBuilderSwapTab(activePageId, deviceView, node.id, {
        tabsetId: node.id,
        prevIndex,
        nextIndex,
      }),
    );
  }, [activePageId, deviceView, node.id]);

  function setDefaultTab(tabId: string) {
    if (
      tabId !== settings.defaultTabId &&
      node.children.some(tab => tab.id === tabId)
    ) {
      dispatch(
        websiteBuilderUpdateContainerSettings(activePageId, deviceView, node.id, {
          id: node.id,
          prevContainerSettings: {
            type: "tabset",
            settings: cloneDeep(settings),
          },
          nextContainerSettings: {
            type: "tabset",
            settings: {
              ...cloneDeep(settings),
              defaultTabId: tabId,
            },
          },
        }),
      );

      setSelectedTab(tabId);
    }
  }

  function startRenamingTab(tabId: string) {
    const tab = node.children.find(tab => tab.id === tabId);

    if (tab == null) {
      return;
    }

    setRenameTabState({
      id: tab.id,
      title: {
        value: tab.settings.title,
        dirty: false,
        valid: tab.settings.title.trim().length > 0,
      },
    });

    setSelectedTab(tabId);
  }

  function cancelRenamingTab() {
    setRenameTabState(null);
  }

  function renameTab() {
    if (
      renameTabState == null ||
      !renameTabState.title.valid
    ) {
      return;
    }

    const tab = node.children.find(tab => tab.id === renameTabState.id);

    if (tab == null) {
      return;
    }

    const title = renameTabState.title.value.trim();

    if (title !== tab.settings.title) {
      dispatch(
        websiteBuilderUpdateTabSettings(activePageId, deviceView, node.id, {
          tabsetId: node.id,
          tabId: tab.id,
          prevTabSettings: cloneDeep(tab.settings),
          nextTabSettings: {
            ...cloneDeep(tab.settings),
            title,
          },
        }),
      );
    }

    cancelRenamingTab();
  }

  function renderTabList() {
    if (node.children.length === 0) {
      return <div className="website-builder-tabset-settings-empty">{t("No tabs added yet")}</div>;
    }

    return (
      <div className="website-builder-tabset-settings-list">
        {node.children.map((tab, index) => {
          const active = activeTabId === tab.id;
          const renaming = renameTabState?.id === tab.id;

          return (
            <TabsetSettingsListItem
              key={tab.id}
              tabsetId={node.id}
              index={index}
              active={active}
              renaming={renaming}
              swapTab={swapTab}
              onClick={() => setSelectedTab(tab.id)}
            >
              {renaming ? (
                <div
                  ref={renameTabRef}
                  className="tabset-settings-list-item-rename"
                >
                  <input
                    className={classNames("tabset-settings-list-item-rename-input", {
                      "invalid": renameTabState.title.dirty && !renameTabState.title.valid,
                    })}
                    type="text"
                    value={renameTabState.title.value}
                    autoFocus
                    onChange={e => {
                      const value = e.target.value;

                      setRenameTabState(prev => {
                        return {
                          ...prev,
                          title: {
                            value,
                            dirty: true,
                            valid: value.trim().length > 0,
                          },
                        };
                      });
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === "Enter") {
                        event.stopPropagation();
                        renameTab();
                      } else if (event.key === "Escape") {
                        event.stopPropagation();
                        cancelRenamingTab();
                      }
                    }}
                  />
                  <Button
                    size="xsmall"
                    type="primary"
                    onClick={renameTab}
                    disabled={!renameTabState.title.valid}
                  >
                    {t("Done")}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="tabset-settings-list-item-title-container">
                    <div className="tabset-settings-list-item-title">{tab.settings.title}</div>
                    {settings.defaultTabId === tab.id && (
                      <FontAwesomeIcon
                        className="tabset-settings-list-item-default"
                        icon={faCircleDot}
                      />
                    )}
                  </div>
                  <NavigationDropdownNew
                    hideChevron
                    showPlainTextLabel
                    rightAlign
                    label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                    sections={[
                      [
                        {
                          label: t("Duplicate Tab"),
                          icon: faCopy.iconName,
                          disabled: maxTabsReached,
                          onClick: () => duplicateTab(tab.id),
                        },
                        {
                          label: t("Set as default"),
                          icon: faCircleDot.iconName,
                          disabled: settings.defaultTabId === tab.id,
                          onClick: () => setDefaultTab(tab.id),
                        },
                        {
                          label: t("Rename"),
                          icon: faPen.iconName,
                          onClick: () => startRenamingTab(tab.id),
                        },
                      ],
                      [
                        {
                          label: t("Delete"),
                          icon: faTrashCan.iconName,
                          color: "red",
                          onClick: () => deleteTab(tab.id),
                        },
                      ],
                    ]}
                  />
                </>
              )}
            </TabsetSettingsListItem>
          );
        })}
      </div>
    );
  }

  function renderActions() {
    return (
      <div className="website-builder-tabset-settings-actions">
        <Button
          size="small"
          type="secondary"
          onClick={() => duplicateTab(activeTabId)}
          disabled={activeTabId == null || maxTabsReached}
        >
          {t("Duplicate")}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={addTab}
          disabled={maxTabsReached}
        >
          {t("Add Tab")}
        </Button>
      </div>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Manage Tabs")}
      closePanel={closeSettings}
      sections={[renderTabList()]}
      footer={renderActions()}
    />
  );
}

interface ITabsetSettingsListItemProps {
  tabsetId: string;
  index: number;
  active: boolean;
  renaming: boolean;
  swapTab: (prevIndex: number, nextIndex: number) => void;
  onClick: () => void;
  children: React.ReactNode;
}

interface ITabsetSettingsListItemDragState {
  isDragging: boolean;
}

interface ITabsetSettingsListItemDropState {
  canDrop: boolean;
  isOver: boolean;
}

interface ITabsetSettingsListItemDragItem {
  tabsetId: string;
  srcIndex: number;
}

const WEBSITE_BUILDER_TABSET_SETTINGS_LIST_ITEM_DROP_ITEM = "website-builder-tabset-settings-list-item";

function TabsetSettingsListItem(props: ITabsetSettingsListItemProps) {
  const { tabsetId, index, active, renaming, swapTab, onClick, children } = props;

  const [dragState, drag, dragPreview] = useDrag<ITabsetSettingsListItemDragItem, unknown, ITabsetSettingsListItemDragState>(
    () => ({
      type: WEBSITE_BUILDER_TABSET_SETTINGS_LIST_ITEM_DROP_ITEM,
      item: {
        tabsetId,
        srcIndex: index,
      },
      canDrag: !renaming,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [tabsetId, index, renaming],
  );

  const [dropState, drop] = useDrop<ITabsetSettingsListItemDragItem, unknown, ITabsetSettingsListItemDropState>(
    () => ({
      accept: WEBSITE_BUILDER_TABSET_SETTINGS_LIST_ITEM_DROP_ITEM,
      canDrop: item => item.tabsetId === tabsetId && item.srcIndex !== index,
      drop: item => swapTab(item.srcIndex, index),
      collect: monitor => {
        // Avoid React DnD's "Expected to find a valid target" error when the panel closes on an unrelated drag start.
        const draggingTabsetSettingsListItem =
          monitor.getItemType() === WEBSITE_BUILDER_TABSET_SETTINGS_LIST_ITEM_DROP_ITEM;

        return {
          canDrop: draggingTabsetSettingsListItem && monitor.canDrop(),
          isOver: draggingTabsetSettingsListItem && monitor.isOver(),
        };
      },
    }),
    [tabsetId, index, swapTab],
  );

  return (
    <div
      ref={el => {
        drop(el);
        dragPreview(el);
      }}
      className={classNames("tabset-settings-list-item", {
        "dragging": dragState.isDragging,
        "drop-target": dropState.canDrop && dropState.isOver,
        "active": active,
      })}
      onClick={onClick}
    >
      {!renaming && (
        <div ref={drag} className="tabset-settings-list-item-grip-container">
          <FontAwesomeIcon className="tabset-settings-list-item-grip" icon={faGrip} />
        </div>
      )}
      {children}
    </div>
  );
}